import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, MessageCircle, Smartphone, ShoppingBag, ClipboardList,
  Bot, Zap, Settings, LogOut, Loader2,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { getMe, logout } from "@/lib/api";
import { avatarColor, initials } from "@/lib/format";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Inicio", id: "nav-inicio" },
  { to: "/chats", icon: MessageCircle, label: "Chats", id: "nav-chats" },
  { to: "/simulador", icon: Smartphone, label: "Simulador", id: "nav-simulador" },
  { to: "/catalogo", icon: ShoppingBag, label: "Catálogo", id: "nav-catalogo" },
  { to: "/pedidos", icon: ClipboardList, label: "Pedidos", id: "nav-pedidos" },
  { to: "/entrenar", icon: Bot, label: "Entrenar bot", id: "nav-entrenar" },
  { to: "/respuestas", icon: Zap, label: "Respuestas rápidas", id: "nav-respuestas" },
  { to: "/ajustes", icon: Settings, label: "WhatsApp y ajustes", id: "nav-ajustes" },
];

function RailItem({ item }) {
  const Icon = item.icon;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <NavLink
          to={item.to}
          aria-label={item.label}
          data-testid={item.id}
          className={({ isActive }) =>
            `relative h-11 w-11 rounded-xl flex items-center justify-center transition-colors ${
              isActive ? "bg-emerald-100 text-emerald-700" : "text-slate-500 hover:bg-slate-200/70 hover:text-slate-800"
            }`
          }
        >
          <Icon className="h-5 w-5" strokeWidth={2} />
        </NavLink>
      </TooltipTrigger>
      <TooltipContent side="right">{item.label}</TooltipContent>
    </Tooltip>
  );
}

export default function AppShell() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    getMe().then(setUser).catch(() => navigate("/", { replace: true }));
  }, [navigate]);

  const handleLogout = async () => {
    try { await logout(); } catch {}
    navigate("/", { replace: true });
  };

  if (!user) {
    return (
      <div className="h-screen flex items-center justify-center text-slate-500 gap-2" data-testid="app-loading">
        <Loader2 className="h-5 w-5 animate-spin" /> Cargando…
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div data-testid="app-shell" className="h-screen flex bg-[#F0F2F5] overflow-hidden">
        <nav className="w-[68px] shrink-0 bg-[#F7F8FA] border-r border-slate-200 flex flex-col items-center py-4 gap-1.5">
          <div className="h-10 w-10 rounded-2xl bg-emerald-500 flex items-center justify-center mb-4 shadow-sm">
            <MessageCircle className="h-5 w-5 text-white" fill="white" />
          </div>
          {NAV.map((n) => <RailItem key={n.to} item={n} />)}
          <div className="mt-auto flex flex-col items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="sign-out-button"
                  aria-label="Cerrar sesión"
                  onClick={handleLogout}
                  className="h-10 w-10 rounded-xl text-slate-500 hover:bg-rose-50 hover:text-rose-600 flex items-center justify-center transition-colors"
                >
                  <LogOut className="h-[18px] w-[18px]" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Cerrar sesión</TooltipContent>
            </Tooltip>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" data-testid="current-user-avatar" aria-label="Abrir menú de cuenta"
                  className={`h-9 w-9 rounded-full text-white text-xs font-bold flex items-center justify-center cursor-pointer hover:ring-4 hover:ring-slate-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-emerald-300 ${avatarColor(user.email)}`}>
                  {initials(user.name, user.email)}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="right" align="end" sideOffset={12} className="w-64 max-w-[calc(100vw-88px)] rounded-xl p-2">
                <DropdownMenuLabel className="space-y-1">
                  <p className="font-semibold break-words">{user.name}</p>
                  <p className="text-xs font-normal text-slate-500 break-all">{user.email}</p>
                  <p className="text-xs font-normal text-emerald-700">{user.role === "owner" ? "Propietario" : "Agente"}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate("/ajustes")} className="cursor-pointer"><Settings className="mr-2 h-4 w-4" />WhatsApp y ajustes</DropdownMenuItem>
                <DropdownMenuItem asChild><a href="https://myaccount.google.com/" target="_blank" rel="noreferrer">Mi cuenta de Google</a></DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={handleLogout} className="cursor-pointer text-rose-600"><LogOut className="mr-2 h-4 w-4" />Cerrar sesión</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </nav>
        <main className="flex-1 min-w-0 h-full overflow-hidden flex flex-col">
          {process.env.REACT_APP_DEMO_MODE === "true" && (
            <div role="status" className="shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
              Demostración local · respuestas de prueba, sin envíos a WhatsApp
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-hidden">
            <Outlet context={{ user }} />
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}
